import React, { FC, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { css } from 'styled-components'
import { isOfFamily } from '@feedyou/utils'
import { ReleaseFamily } from '@feedyou-ai/feedbot-schema'
import * as semver from 'semver'

import { Button, ButtonType } from '../../../../../../../Styleguide/Button'
import {
  hostingErrorSelector,
  useIsHostingVersionUpdating
} from '../../../../../../../../store/socketServer'
import { useUpdateToLatestVersion } from '../actions'
import { botHostingMetadataQueryStateSelector } from '../../../../../../../../features/hostingVersions/selectors/botHostingMetadataSelector'
import { useSdkBuildInfo } from '../../../../../../../../features/hostingVersions/hooks/useSdkBuildInfo'
import { truncateCommit } from '../../../../../../../../features/hostingVersions'

export const UpdateToLatestButton: FC = () => {
  const updateToLatest = useUpdateToLatestVersion()

  return (
    <Button
      {...useButtonState()}
      className="e2e-update-button"
      onClick={e => {
        e.preventDefault()
        updateToLatest()
      }}
      css={buttonCss}
      onClickWhenDisabled={e => e.preventDefault()}
      buttonType={ButtonType.DEFAULT}
      key={'action-button-update'}
    />
  )
}

type ButtonState = {
  loading: boolean
  disabled: boolean
  text: ReactNode
  error?: string | React.JSX.Element
  tooltipText?: string
}

const buttonCss = css`
  min-width: unset;
`

export const useButtonState = (): ButtonState => {
  const isUpdating = useIsHostingVersionUpdating()
  const isLoading = useSelector(botHostingMetadataQueryStateSelector)?.isLoading
  const updateError = useSelector(hostingErrorSelector)

  const deployedVersion = useSdkBuildInfo()?.deployedVersion
  const { latestCommit, latestVersion } = useSdkBuildInfo() ?? {}

  if (isLoading) {
    return { loading: true, disabled: true, text: 'Checking for updates...' }
  }

  if (isUpdating) {
    return { loading: true, disabled: true, text: 'Updating ...' }
  }

  if (updateError) {
    return {
      loading: false,
      disabled: false,
      text: 'Update failed - try again',
      error: (
        <>
          Update failed with error: {updateError} <br /> <br />
          Click again to retry.
        </>
      )
    }
  }

  if (!deployedVersion?.releaseFamily) {
    return {
      loading: false,
      disabled: true,
      text: 'Failed to load data for currently deployed version'
    }
  }

  // latest can e used with v1.7, too
  if (isOfFamily(ReleaseFamily.Master, ReleaseFamily.Feature)(deployedVersion)) {
    if (!deployedVersion.commit) {
      return {
        loading: false,
        disabled: false,
        text: (
          <>
            <span css={alertIconPositionCss}>⚠️</span> Cannot detect commit
          </>
        ),
        tooltipText: `Your current sdk version does not support reporting current commit, or it cannot not be produced for your branch. You can try updating (clicking this button), to fix this. But if this doesn't help, you will need to contact support.`
      }
    }

    if (!latestCommit) {
      return {
        loading: false,
        disabled: true,
        text: 'Failed to fetch latest version',
        tooltipText: `The latest commit for current build could not be detected. Please contact support.`
      }
    }

    if (deployedVersion.commit !== latestCommit) {
      return {
        loading: false,
        disabled: false,
        text: 'Update to newest commit',
        tooltipText: `Update from commit ${truncateCommit(deployedVersion.commit)} 
        to ${truncateCommit(latestCommit)}`
      }
    }

    return { loading: false, disabled: true, text: 'Up to date 🎉' }
  }

  if (!latestVersion) {
    return {
      loading: false,
      disabled: true,
      text: 'Failed to fetch latest version'
    }
  }

  if (deployedVersion.version !== latestVersion) {
    const isAheadOfLatest = semver.gt(deployedVersion.version, latestVersion)

    return {
      loading: false,
      disabled: false,
      text: isAheadOfLatest
        ? `Revert to v${latestVersion} (latest publicly released version)`
        : `Update to v${latestVersion}`
    }
  }

  return { loading: false, disabled: true, text: 'Up to date 🎉' }
}

const alertIconPositionCss = css`
  font-size: 12px;
  margin-left: -9px;
  margin-right: 3px;
  display: inline-block;
  transform: translateY(-1px);
`
